/**
 * Public-read API client for the status page. Thin `fetch` wrappers over the
 * no-auth `/api/v1/*` endpoints — extracted from `App.tsx` so the data layer
 * is separable from the view. Types come from `status-types.ts`.
 *
 * Note: `api.uptime` keeps its own `fetchJson` (vs `lib/uptime-api.ts`'s
 * `fetchUptimeWindow`) — both target the same URL and share the React-Query
 * cache key `uptimeQueryKey(id, window, anchor)`, so they dedupe.
 */
import type {
  ComponentDetail,
  ComponentGroup,
  OverallStatus,
  UptimeWindow,
  UptimeWindowResponse,
} from './status-types';

export async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Request to ${url} failed (${String(response.status)})`);
  }
  return (await response.json()) as T;
}

export const api = {
  status: () => fetchJson<OverallStatus>('/api/v1/status'),
  components: () => fetchJson<ComponentGroup[]>('/api/v1/components'),
  component: (id: number) =>
    fetchJson<ComponentDetail>(`/api/v1/components/${String(id)}`),
  uptime: (id: number, window: UptimeWindow, anchor?: string) => {
    const params = new URLSearchParams({ window });
    if (anchor != null && anchor !== '') params.set('anchor', anchor);
    return fetchJson<UptimeWindowResponse>(
      `/api/v1/components/${String(id)}/uptime?${params.toString()}`,
    );
  },
};
